import { useEffect, useRef, useState, useCallback } from 'react';

import LoadingWrapper from '../../components/LoadingWrapper';
import StandardWorkTrainerTable from '../../components/StandardWorkerTrainer/StandardWorkTrainerTable';
import { errorToast } from '../../components/Toasty';

import { userCanAddStandardWorkTrainer, userCanEditStandardWorkTrainer } from '../../functions/AccessHelpers';
import { isStandardWorkTrainerCert } from '../../functions/CertificationHelpers';
import { getEmployeeCertifications, getWorkdayCertifications } from '../../services/Workday/WorkdayCertificationService';

import { WorkdayCertification } from '../../types/Workday/WorkdayCertification';
import { WorkdayEmployeeCertification } from '../../types/Workday/WorkdayEmployeeCertification';
import EmployeeInfo from '../../types/Employee/EmployeeInfo';
import IProject from '../../types/Dashboard/Project';

type Props = {
    userData: EmployeeInfo;
    isMocked: boolean;
    projectList?: IProject[];
};

const StandardWorkTrainers = ({ userData, isMocked, projectList = [] }: Props) => {
    const [employeeCertList, setEmployeeCertList] = useState<WorkdayEmployeeCertification[]>([]);
    const [allWorkdayCertifications, setAllWorkdayCertifications] = useState<WorkdayCertification[]>([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [userCanAdd, setUserCanAdd] = useState(false);
    const [userCanEdit, setUserCanEdit] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const loadData = useCallback(async () => {
        setDataLoading(true);
        try {
            const [empCertResult, certResult] = await Promise.all([
                getEmployeeCertifications(),
                getWorkdayCertifications(),
            ]);

            if (!mountedRef.current) return;

            if (empCertResult.ok && empCertResult.employeeCertifications) {
                setEmployeeCertList(empCertResult.employeeCertifications.filter(ec => isStandardWorkTrainerCert(ec.certification.name)));
            } else {
                errorToast(empCertResult.message ?? 'Error loading employee certifications');
            }

            if (certResult.ok && certResult.certifications) {
                setAllWorkdayCertifications(certResult.certifications.filter(c => isStandardWorkTrainerCert(c.name)));
            } else {
                errorToast(certResult.message ?? 'Error loading certifications');
            }
        } finally {
            if (mountedRef.current) setDataLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userData.employeeID === 0) return;

        setUserCanAdd(userCanAddStandardWorkTrainer(userData));
        setUserCanEdit(userCanEditStandardWorkTrainer(userData));
        loadData();
    }, [userData, loadData]);

    const handleSaveSuccess = async () => {
        await loadData();
    };

    return (
        <div className='standard-work-trainer-page'>
            <div className='row border-bottom mx-3'>
                <div className='col-12 text-center h2 my-3'>
                    Standard Work Trainers
                </div>
            </div>
            <div className='row pt-1 mt-1'>
                <LoadingWrapper
                    windSpinner
                    windSpinnerScale={3}
                    showLoading={dataLoading}
                    loadingMessage='Loading'
                >
                    <StandardWorkTrainerTable
                        employeeCertList={employeeCertList}
                        allWorkdayCertifications={allWorkdayCertifications}
                        projectList={projectList}
                        userCanAdd={userCanAdd}
                        userCanEdit={userCanEdit}
                        userEmail={userData.email}
                        isMocked={isMocked}
                        onSaveSuccess={handleSaveSuccess}
                    />
                </LoadingWrapper>
            </div>
        </div>
    );
};

export default StandardWorkTrainers;
